export interface TenantSettings {
  projectName?: string;
  amazonTag: string;
  magaluPartnerId: string;
  mercadolivreTag: string;
  mercadolivreAppId: string;
  mercadolivreSecretKey: string;
  mercadolivreAccessToken: string;
  shopeeAppId: string;
  shopeeSecret: string;
  publicTitle?: string;
  slogan?: string;
  logoUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
  metaPixelId?: string;
  ga4MeasurementId?: string;
  privacyPolicyUrl?: string;
  showcaseExpiryWarningDays?: number;
  tenantSlug?: string;
  defaultLinkType?: 'api' | 'marketplace';
}

export interface TenantPublicBranding {
  name: string;
  slug: string;
  publicTitle: string;
  slogan?: string;
  logoUrl?: string;
  primaryColor: string;
  secondaryColor: string;
  metaPixelId?: string;
  ga4MeasurementId?: string;
  privacyPolicyUrl?: string;
}

const API_BASE_URL = '';

export async function fetchTenantSettings(
  token: string
): Promise<TenantSettings> {
  const response = await fetch(`${API_BASE_URL}/api/settings`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Erro ao carregar configurações (${response.status})`);
  }

  const data = await response.json();
  return {
    projectName: data.project_name || 'Achadinhos Tech',
    amazonTag: data.amazon_tag || 'affiliator-20',
    magaluPartnerId: data.magalu_partner_id || 'affiliator',
    mercadolivreTag: data.mercadolivre_tag || 'affiliator',
    mercadolivreAppId: data.mercadolivre_app_id || '',
    mercadolivreSecretKey: data.mercadolivre_secret_key || '',
    mercadolivreAccessToken: data.mercadolivre_access_token || '',
    shopeeAppId: data.shopee_app_id || '',
    shopeeSecret: data.shopee_secret || '',
    publicTitle: data.public_title || data.project_name || 'Achadinhos Tech',
    slogan:
      data.slogan ||
      'As melhores ofertas e promoções da internet em tempo real.',
    logoUrl: data.logo_url || '',
    primaryColor: data.primary_color || '#2563EB',
    secondaryColor: data.secondary_color || '#10B981',
    metaPixelId: data.meta_pixel_id || '',
    ga4MeasurementId: data.ga4_measurement_id || '',
    privacyPolicyUrl: data.privacy_policy_url || '',
    showcaseExpiryWarningDays: data.showcase_expiry_warning_days ?? 2,
    tenantSlug: data.tenant_slug || undefined,
    defaultLinkType: data.default_link_type || 'api',
  };
}

export async function updateTenantSettings(
  token: string,
  settings: Partial<TenantSettings>
): Promise<TenantSettings> {
  const payload = {
    project_name: settings.projectName,
    amazon_tag: settings.amazonTag,
    magalu_partner_id: settings.magaluPartnerId,
    mercadolivre_tag: settings.mercadolivreTag,
    mercadolivre_app_id: settings.mercadolivreAppId,
    mercadolivre_secret_key: settings.mercadolivreSecretKey,
    mercadolivre_access_token: settings.mercadolivreAccessToken,
    shopee_app_id: settings.shopeeAppId,
    shopee_secret: settings.shopeeSecret,
    public_title: settings.publicTitle,
    slogan: settings.slogan,
    logo_url: settings.logoUrl,
    primary_color: settings.primaryColor,
    secondary_color: settings.secondaryColor,
    meta_pixel_id: settings.metaPixelId,
    ga4_measurement_id: settings.ga4MeasurementId,
    privacy_policy_url: settings.privacyPolicyUrl,
    showcase_expiry_warning_days: settings.showcaseExpiryWarningDays,
    default_link_type: settings.defaultLinkType,
  };

  const response = await fetch(`${API_BASE_URL}/api/settings`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Erro ao atualizar configurações (${response.status})`);
  }

  const data = await response.json();
  return {
    projectName: data.project_name || 'Achadinhos Tech',
    amazonTag: data.amazon_tag || 'affiliator-20',
    magaluPartnerId: data.magalu_partner_id || 'affiliator',
    mercadolivreTag: data.mercadolivre_tag || 'affiliator',
    mercadolivreAppId: data.mercadolivre_app_id || '',
    mercadolivreSecretKey: data.mercadolivre_secret_key || '',
    mercadolivreAccessToken: data.mercadolivre_access_token || '',
    shopeeAppId: data.shopee_app_id || '',
    shopeeSecret: data.shopee_secret || '',
    publicTitle: data.public_title || data.project_name || 'Achadinhos Tech',
    slogan:
      data.slogan ||
      'As melhores ofertas e promoções da internet em tempo real.',
    logoUrl: data.logo_url || '',
    primaryColor: data.primary_color || '#2563EB',
    secondaryColor: data.secondary_color || '#10B981',
    metaPixelId: data.meta_pixel_id || '',
    ga4MeasurementId: data.ga4_measurement_id || '',
    privacyPolicyUrl: data.privacy_policy_url || '',
    showcaseExpiryWarningDays: data.showcase_expiry_warning_days ?? 2,
    defaultLinkType: data.default_link_type || 'api',
  };
}

export async function fetchTenantPublicBranding(
  slug: string = 'default'
): Promise<TenantPublicBranding> {
  const response = await fetch(
    `${API_BASE_URL}/api/settings/public/${encodeURIComponent(slug)}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Erro ao carregar branding do canal (${response.status})`);
  }

  const data = await response.json();
  return {
    name: data.name,
    slug: data.slug,
    publicTitle: data.public_title,
    slogan: data.slogan,
    logoUrl: data.logo_url,
    primaryColor: data.primary_color || '#2563EB',
    secondaryColor: data.secondary_color || '#10B981',
    metaPixelId: data.meta_pixel_id,
    ga4MeasurementId: data.ga4_measurement_id,
    privacyPolicyUrl: data.privacy_policy_url,
  };
}
